import type { LocalWidgetContainerData, LocalWidgetStorageService } from '.';
import { injectable, inject } from 'src/features/ioc';
import type { SyncStorageService } from 'src/services/SyncStorageService';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'LocalWidgetStorageService' });

@injectable()
export class LocalWidgetStorageServiceImpl implements LocalWidgetStorageService {
    constructor(@inject('SyncStorageService') private storage: SyncStorageService) {}

    private getStorageKey(workspaceId: string): string {
        return `localWidgets:${workspaceId}`;
    }

    saveLocalWidgets(workspaceId: string, containers: LocalWidgetContainerData[]): void {
        try {
            const key = this.getStorageKey(workspaceId);
            const data = JSON.stringify(containers);
            this.storage.setItem(key, data);
        } catch (error) {
            log.error('Failed to save local widgets to localStorage', { error, workspaceId });
        }
    }

    loadLocalWidgets(workspaceId: string): LocalWidgetContainerData[] {
        try {
            const key = this.getStorageKey(workspaceId);
            const data = this.storage.getItem(key);
            if (!data) {
                return [];
            }
            return JSON.parse(data) as LocalWidgetContainerData[];
        } catch (error) {
            log.error('Failed to load local widgets from localStorage', { error, workspaceId });
            return [];
        }
    }

    cleanupStaleWidgets(workspaceId: string, validLocalWidgetTypes: Set<string>): void {
        try {
            const containers = this.loadLocalWidgets(workspaceId);
            const filteredContainers = containers
                .map((container) => {
                    const tabs = container.tabs.filter((tab) => validLocalWidgetTypes.has(tab.widget.type));
                    return {
                        ...container,
                        tabs,
                        activeTabId:
                            container.activeTabId && tabs.some((tab) => tab.id === container.activeTabId)
                                ? container.activeTabId
                                : (tabs[0]?.id ?? null),
                    };
                })
                .filter((container) => container.tabs.length > 0);

            const didChange =
                filteredContainers.length !== containers.length ||
                filteredContainers.some((container, index) => {
                    const originalContainer = containers[index];
                    return (
                        container.activeTabId !== originalContainer?.activeTabId ||
                        container.tabs.length !== originalContainer?.tabs.length
                    );
                });

            if (didChange) {
                this.saveLocalWidgets(workspaceId, filteredContainers);
            }
        } catch (error) {
            log.error('Failed to cleanup stale widgets', { error, workspaceId });
        }
    }
}
